import {
  GeckoComponentModelType,
  GeckoMeasurementUnit,
  type GeckoComponentModel,
} from 'types'

import type { NormalizedBounds } from './types'

// Match the 2D AssetViz scene surfaces that project angle/radius data onto
// a circular radial plane instead of an unwrapped rectangle.
const RADIAL_POLAR_COMPONENT_TYPES = new Set([
  GeckoComponentModelType.LegacyCone,
  GeckoComponentModelType.Cone,
])

const COMPASS_LABELS = new Map<number, string>([
  [0, 'N'],
  [90, 'W'],
  [180, 'S'],
  [270, 'E'],
])

export function isAngleUnit(unit: GeckoMeasurementUnit) {
  return (
    unit === GeckoMeasurementUnit.Degrees ||
    unit === GeckoMeasurementUnit.Radians
  )
}

export function toDegrees(value: number, unit: GeckoMeasurementUnit) {
  if (unit === GeckoMeasurementUnit.Radians) {
    return (value * 180) / Math.PI
  }

  return value
}

export function isPolarCircularHeatmap(
  componentModel: GeckoComponentModel,
  bounds: NormalizedBounds
) {
  return (
    RADIAL_POLAR_COMPONENT_TYPES.has(componentModel.type) &&
    isAngleUnit(bounds.rect.x.unit)
  )
}

export function getCompassOrientationDegrees(
  componentModel: GeckoComponentModel
) {
  return 'compassOrientation' in componentModel &&
    typeof componentModel.compassOrientation === 'number'
    ? componentModel.compassOrientation
    : 0
}

export function getCompassLabels(
  compassOrientationDegrees: number,
  angleDeltaDegrees: 1 | 2 | 3 | 5 | 6 | 9 | 10 | 15 | 18 | 30 | 45 | 90 = 10
) {
  return Array.from({ length: 360 / angleDeltaDegrees }, (_, index) => {
    const baseAngleDegrees = index * angleDeltaDegrees

    return {
      label:
        COMPASS_LABELS.get(baseAngleDegrees) ?? `${360 - baseAngleDegrees}°`,
      positionDegrees:
        (baseAngleDegrees + compassOrientationDegrees + 360) % 360,
    }
  })
}

export function hasFullPolarSweep(bounds: NormalizedBounds) {
  const xStart = toDegrees(
    bounds.rect.x.min.toNumber(bounds.rect.x.unit),
    bounds.rect.x.unit
  )
  const xEnd = toDegrees(
    bounds.rect.x.max.toNumber(bounds.rect.x.unit),
    bounds.rect.x.unit
  )

  return Math.abs(xEnd - xStart) >= 359.5
}

export function polarToCanvasPoint(
  centerX: number,
  centerY: number,
  radius: number,
  angleDegrees: number
) {
  const angleRadians = (angleDegrees * Math.PI) / 180

  return {
    x: centerX - Math.sin(angleRadians) * radius,
    y: centerY - Math.cos(angleRadians) * radius,
  }
}

export function toCanvasAngleRadians(angleDegrees: number) {
  return -Math.PI / 2 - (angleDegrees * Math.PI) / 180
}
